// src/screens/GuestProfile.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReviewsSection from "../components/ReviewsSection";
import "../styles/GuestProfile.css";

/**
 * IMPORTANT:
 * - do NOT include /api in REACT_APP_API_URL
 * - we will call `${API_BASE}/api/...`
 */
const API_BASE = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const envBase = clean(process.env.REACT_APP_API_URL);
  if (envBase) return envBase;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  if (origin.includes("pay2pee.app")) return "https://pay2pee.onrender.com";

  return origin;
})();

function InitialsAvatar({ name = "Guest", className = "profile-photo" }) {
  const initials = useMemo(() => {
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0]?.[0] || "G") + (parts[1]?.[0] || "")).toUpperCase();
  }, [name]);

  return (
    <div
      className={className}
      style={{
        width: 80,
        height: 80,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #2563eb, #ff7b54)",
        color: "#fff",
        fontWeight: 800,
        fontSize: 28,
      }}
    >
      {initials}
    </div>
  );
}

export default function GuestProfile() {
  // ✅ THIS hook must be inside the component
  useEffect(() => {
    document.body.classList.add("p2p-fullwidth", "p2p-guestprofile");
    return () =>
      document.body.classList.remove("p2p-fullwidth", "p2p-guestprofile");
  }, []);

  // Auth (read token fresh per render)
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  // Identity / profile basics (local cache for snappy UI)
  const [userName, setUserName] = useState(
    (typeof window !== "undefined" && localStorage.getItem("userName")) ||
      "Guest"
  );
  const [avatarUrl, setAvatarUrl] = useState(
    (typeof window !== "undefined" && localStorage.getItem("avatarUrl")) || ""
  );
  const [jobTitle, setJobTitle] = useState(
    (typeof window !== "undefined" && localStorage.getItem("jobTitle")) || ""
  );
  const [memberSince, setMemberSince] = useState(
    (typeof window !== "undefined" && localStorage.getItem("memberSince")) ||
      ""
  );

  // Real data
  const [recentVisits, setRecentVisits] = useState([]);
  const [reviewsForSection, setReviewsForSection] = useState([]);
  const [loadingProfileData, setLoadingProfileData] = useState(false);

  // Edit profile modal state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(userName);
  const [editJob, setEditJob] = useState(jobTitle);
  const [editAvatar, setEditAvatar] = useState(avatarUrl);

  const memberSinceLabel = memberSince
    ? new Date(memberSince).toLocaleDateString()
    : "—";

  const persistLocal = (key, value) => {
    try {
      if (typeof window !== "undefined") localStorage.setItem(key, value);
    } catch (err) {
      console.warn(`localStorage setItem failed for ${key}`, err);
    }
  };

  const removeLocal = (key) => {
    try {
      if (typeof window !== "undefined") localStorage.removeItem(key);
    } catch (err) {
      console.warn(`localStorage removeItem failed for ${key}`, err);
    }
  };

  // ✅ Build swipe cards from ALL visits, grouped by location
  const buildVisitCards = (visits, payload) => {
    const grouped = {};

    (visits || []).forEach((v) => {
      const locationKey =
        v.locationId || v.locationName || v.name || "unknown_location";

      const bestName =
        v.locationName ||
        v.location?.name ||
        (v.name && v.name !== "Bathroom visit" ? v.name : "") ||
        "Bathroom";

      const bestAddress =
        v.locationAddress ||
        v.location?.address ||
        v.address ||
        "";

      if (!grouped[locationKey]) {
        grouped[locationKey] = {
          id: locationKey,
          locationId: v.locationId || null,

          bathroomName: bestName,
          address: bestAddress,
          timestamp: v.date || null,

          timesVisited: 0,
          hasReview: !!(v.review?.rating || v.review?.text),
          rating: Number(v.review?.rating || 0),
          comment: v.review?.text || "",

          // NOTE: ReviewsSection should show location name/address, not guestName
          guestName: payload?.name || "Guest",
          avatar: payload?.avatarUrl || "",
        };
      }

      grouped[locationKey].timesVisited += 1;

      if (
        v.date &&
        (!grouped[locationKey].timestamp ||
          new Date(v.date).getTime() >
            new Date(grouped[locationKey].timestamp).getTime())
      ) {
        grouped[locationKey].timestamp = v.date;
        grouped[locationKey].bathroomName =
          bestName || grouped[locationKey].bathroomName;
        grouped[locationKey].address =
          bestAddress || grouped[locationKey].address;
      }

      if (v.review?.rating || v.review?.text) {
        grouped[locationKey].hasReview = true;
        if (v.review?.rating)
          grouped[locationKey].rating = Number(v.review.rating);
        if (v.review?.text)
          grouped[locationKey].comment = v.review.text;
      }
    });

    return Object.values(grouped).sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
  };

  // ---------- Fetch profile ----------
  useEffect(() => {
    if (!API_BASE || !token) return;

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        setLoadingProfileData(true);

        const res = await axios.get(`${API_BASE}/api/users/profile`, {
          headers: authHeader,
        });

        if (cancelled) return;

        const payload = res.data || {};

        if (payload.uid) localStorage.setItem("uid", payload.uid);
        else if (payload.userId) localStorage.setItem("uid", payload.userId);

        if (payload.name) {
          setUserName(payload.name);
          persistLocal("userName", payload.name);
          if (typeof window !== "undefined")
            window.dispatchEvent(new Event("auth:update"));
        }

        if (typeof payload.avatarUrl === "string") {
          setAvatarUrl(payload.avatarUrl);
          persistLocal("avatarUrl", payload.avatarUrl);
          if (typeof window !== "undefined")
            window.dispatchEvent(new Event("auth:update"));
        }

        if (typeof payload.jobTitle === "string") {
          setJobTitle(payload.jobTitle);
          persistLocal("jobTitle", payload.jobTitle);
        }

        if (payload.memberSince) {
          setMemberSince(payload.memberSince);
          persistLocal("memberSince", payload.memberSince);
        } else if (!memberSince) {
          const joined = new Date().toISOString();
          setMemberSince(joined);
          persistLocal("memberSince", joined);
        }

        if (Array.isArray(payload.recentVisits)) {
          setRecentVisits(payload.recentVisits);
          setReviewsForSection(buildVisitCards(payload.recentVisits, payload));
        } else {
          setRecentVisits([]);
          setReviewsForSection([]);
        }
      } catch (e) {
        console.error("GET /api/users/profile error:", e);
      } finally {
        if (!cancelled) setLoadingProfileData(false);
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [API_BASE, token]);

  // ---------- Avatar rendering w/ fallback ----------
  const profileAvatar =
    avatarUrl && avatarUrl.length > 0 ? (
      <img
        src={avatarUrl}
        alt={userName}
        className="profile-photo"
        onError={() => {
          setAvatarUrl("");
          removeLocal("avatarUrl");
        }}
      />
    ) : (
      <InitialsAvatar name={userName} className="profile-photo" />
    );

  const handleOpenEdit = () => {
    setEditName(userName);
    setEditJob(jobTitle);
    setEditAvatar(avatarUrl);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    const name = editName.trim() || "Guest";
    const job = editJob.trim();
    const avatar = editAvatar.trim();

    setUserName(name);
    setJobTitle(job);
    setAvatarUrl(avatar);

    persistLocal("userName", name);
    persistLocal("jobTitle", job);
    persistLocal("avatarUrl", avatar);

    if (typeof window !== "undefined")
      window.dispatchEvent(new Event("auth:update"));
    setEditing(false);

    try {
      await axios.put(
        `${API_BASE}/api/users/profile`,
        { name, jobTitle: job, avatarUrl: avatar },
        { headers: authHeader }
      );
    } catch (e) {
      console.error("PUT /api/users/profile error:", e);
    }
  };

  return (
    <div className="p2p-profile">
      <div className="guest-profile demo-version">
        <div className="demo-banner">
          <h2>Guest Profile</h2>
          <p>
            You’re seeing your real account details
            {loadingProfileData ? "…" : ""}.
          </p>
        </div>

        <div className="profile-container">
          <div className="profile-header">
            <div className="profile-main">
              {profileAvatar}
              <div className="profile-info">
                <h1>{userName}</h1>
                {jobTitle ? <p className="job-title">{jobTitle}</p> : null}
                <div className="member-info">
                  <p>Member since {memberSinceLabel}</p>
                </div>
              </div>
            </div>

            <div className="profile-actions">
              <button className="primary-button" onClick={handleOpenEdit}>
                Edit profile
              </button>
            </div>
          </div>

          <div className="subscription-card-container">
            <div className="subscription-card disabled">
              <div className="subscription-header">
                <h3>Subscriptions coming soon</h3>
                <span className="badge-coming-soon">Coming soon</span>
              </div>
              <p>
                Pay-as-you-go is live today. Weekly and monthly passes will be
                rolling out soon so you can tap once and skip checkout for each
                visit.
              </p>
              <p className="coming-soon-note">
                Stay tuned — your current account will automatically support
                passes once they launch.
              </p>
            </div>
          </div>

          <ReviewsSection title="Your Visits" reviews={reviewsForSection} />
        </div>

        {editing && (
          <div className="subscription-modal">
            <div className="modal-content">
              <h3>Edit profile</h3>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 10 }}
                />
              </div>

              <div className="form-group">
                <label>Job / role (optional)</label>
                <input
                  type="text"
                  value={editJob}
                  onChange={(e) => setEditJob(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 10 }}
                />
              </div>

              <div className="form-group">
                <label>Profile image URL (optional)</label>
                <input
                  type="text"
                  value={editAvatar}
                  onChange={(e) => setEditAvatar(e.target.value)}
                  placeholder="https://… or leave blank"
                  style={{ width: "100%", padding: 8, marginBottom: 10 }}
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="cancel-button" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button className="change-plan-button" onClick={handleSaveEdit}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}